import type { CodeLanguage } from "../model/types.js";

export type FileContent =
  | {
      readonly kind: "text";
      readonly text: string;
      readonly language: CodeLanguage;
    }
  | {
      readonly kind: "binary";
      readonly bytes: Uint8Array;
    };

const LANGUAGES_BY_EXTENSION: Readonly<Record<string, CodeLanguage>> = {
  bat: "bat",
  cmd: "bat",
  conf: "text",
  config: "text",
  csv: "csv",
  css: "css",
  gradle: "text",
  groovy: "text",
  htm: "html",
  html: "html",
  ini: "text",
  java: "java",
  js: "javascript",
  cjs: "javascript",
  mjs: "javascript",
  json: "json",
  jsonc: "json",
  kt: "text",
  kts: "text",
  md: "markdown",
  markdown: "markdown",
  mf: "text",
  properties: "text",
  scala: "text",
  sh: "shellscript",
  bash: "shellscript",
  zsh: "shellscript",
  sql: "sql",
  toml: "text",
  ts: "typescript",
  mts: "typescript",
  cts: "typescript",
  txt: "text",
  xml: "xml",
  xsd: "xml",
  xsl: "xml",
  yaml: "yaml",
  yml: "yaml",
};

export function formatByteLength(byteLength: number): string {
  if (byteLength < 1024) {
    return `${byteLength.toLocaleString()} B`;
  }

  const units = ["KB", "MB", "GB"];
  let value = byteLength / 1024;
  let unit = units[0]!;

  for (let index = 1; index < units.length && value >= 1024; index++) {
    value /= 1024;
    unit = units[index]!;
  }

  return `${value >= 10 ? value.toFixed(1) : value.toFixed(2)} ${unit}`;
}

export function languageForPath(path: string): CodeLanguage {
  const normalized = path.replaceAll("\\", "/").toLowerCase();
  const baseName = normalized.slice(normalized.lastIndexOf("/") + 1);

  if (baseName === "manifest.mf") {
    return "text";
  }
  if (
    baseName === "dockerfile" ||
    baseName === "makefile" ||
    baseName === "license" ||
    baseName === "notice"
  ) {
    return "text";
  }

  const dot = baseName.lastIndexOf(".");
  const extension = dot === -1 ? "" : baseName.slice(dot + 1);
  return LANGUAGES_BY_EXTENSION[extension] ?? "text";
}

export function inspectFileContent(
  path: string,
  bytes: Uint8Array,
): FileContent {
  const text = decodeText(bytes);
  return text === null
    ? { kind: "binary", bytes }
    : { kind: "text", text, language: languageForPath(path) };
}

function decodeText(bytes: Uint8Array): string | null {
  if (bytes.length === 0) {
    return "";
  }

  try {
    if (hasPrefix(bytes, [0xff, 0xfe])) {
      return acceptText(new TextDecoder("utf-16le", { fatal: true }).decode(bytes));
    }
    if (hasPrefix(bytes, [0xfe, 0xff])) {
      return acceptText(new TextDecoder("utf-16be", { fatal: true }).decode(bytes));
    }

    const decoded = new TextDecoder("utf-8", { fatal: true }).decode(bytes);
    return acceptText(decoded);
  } catch {
    return null;
  }
}

function acceptText(value: string): string | null {
  let controls = 0;

  for (let index = 0; index < value.length; index++) {
    const code = value.charCodeAt(index);
    if (
      code === 0 ||
      (code < 0x20 && code !== 0x09 && code !== 0x0a && code !== 0x0c && code !== 0x0d)
    ) {
      controls++;
    }
  }

  const controlLimit = Math.max(2, Math.floor(value.length * 0.01));
  return controls > controlLimit ? null : value;
}

function hasPrefix(bytes: Uint8Array, prefix: readonly number[]): boolean {
  return prefix.every((value, index) => bytes[index] === value);
}
